import type { SchemaStationTimetable } from '../../schema/types'

/** `HH:MM`. Anything else the schema carries is not a time (TASK-646). */
const CLOCK_TIME = /^\d{2}:\d{2}$/

/**
 * "Open today" range for the Overview facts list (Figma 8597:44638):
 * the earliest first train to the latest last train across directions,
 * for today's day class (weekday / saturday / sunday).
 *
 * After-midnight last trains (00:xx–02:xx) belong to the evening
 * service, so for the "latest last" comparison they sort after 23:59.
 * Schema v1.29 carries `--:--` literals where TfL data is missing —
 * those drop out; when nothing real remains the row is absent, not a
 * dash (the TASK-589 rule: never publish a fact we do not have).
 */
export function openTodayRange(
  timetable: SchemaStationTimetable | undefined,
  today: Date,
): string | null {
  if (timetable === undefined) return null
  const day = today.getDay()
  const dayClass = day === 0 ? 'sundays' : day === 6 ? 'saturdays' : 'weekdays'

  const firsts: string[] = []
  const lasts: string[] = []
  for (const direction of timetable.directions) {
    const span = direction[dayClass]
    if (span === undefined) continue
    if (span.firstTrain !== undefined && CLOCK_TIME.test(span.firstTrain)) {
      firsts.push(span.firstTrain)
    }
    if (span.lastTrain !== undefined && CLOCK_TIME.test(span.lastTrain)) {
      lasts.push(span.lastTrain)
    }
  }
  if (firsts.length === 0 || lasts.length === 0) return null

  const serviceMinutes = (time: string, afterMidnightLate: boolean): number => {
    const [h, m] = time.split(':').map(Number)
    const minutes = h * 60 + m
    // 00:xx–02:59 on the "last train" axis is tonight's service tail.
    return afterMidnightLate && h < 3 ? minutes + 24 * 60 : minutes
  }

  const first = firsts.reduce((a, b) =>
    serviceMinutes(b, false) < serviceMinutes(a, false) ? b : a,
  )
  const last = lasts.reduce((a, b) => (serviceMinutes(b, true) > serviceMinutes(a, true) ? b : a))
  return `${first} – ${last}`
}
